import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { supabase } from '../../supabase';
import { useAuth } from '../../context/AuthContext';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const MonthlyTotalSpentChart = () => {
  const { user } = useAuth();
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchDailySpending = async () => {
      if (!user?.id) return;

      setLoading(true);
      try {
        const currentDate = new Date();
        const currentMonth = currentDate.getMonth();
        const currentYear = currentDate.getFullYear();
        const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();

        // Fetch expenses for current month
        const startDate = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-01`;
        const endDate = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(daysInMonth).padStart(2, '0')}`;

        const { data: expenses, error } = await supabase
          .from('bill_extracts')
          .select('total_amount, receipt_date')
          .eq('user_id', user.id)
          .gte('receipt_date', startDate)
          .lte('receipt_date', endDate);

        if (error) throw error;

        // Process data
        const dailyTotals = Array.from({ length: daysInMonth }, (_, i) => ({
          day: i + 1,
          amount: 0,
        }));

        expenses.forEach((expense) => {
          const expenseDate = new Date(expense.receipt_date);
          const day = expenseDate.getDate();
          const amount = parseFloat(expense.total_amount) || 0;
          if (day <= daysInMonth) {
            dailyTotals[day - 1].amount += amount;  // Sum all expenses for each day
          }
        });

        // Calculate cumulative totals
        const cumulativeTotals = [];
        let runningTotal = 0;
        for (let i = 0; i < dailyTotals.length; i++) {
          runningTotal += dailyTotals[i].amount;
          cumulativeTotals.push(runningTotal);
        }

        setChartData({
          labels: dailyTotals.map(d => `Day ${d.day}`),
          datasets: [
            {
              label: 'Cumulative Daily Spending',
              data: cumulativeTotals,
              borderColor: 'rgba(34, 135, 197, 1)',
              backgroundColor: 'rgba(34, 135, 197, 0.1)',
              borderWidth: 2,
              fill: true,
              tension: 0.4,
              pointBackgroundColor: 'rgba(34, 135, 197, 1)',
              pointBorderColor: '#fff',
              pointBorderWidth: 2,
              pointRadius: 4,
              pointHoverRadius: 6,
            },
          ],
        });

      } catch (error) {
        console.error('Error fetching daily spending:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDailySpending();
  }, [user?.id]);

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: `Cumulative Daily Spending This Month - ${new Date().toLocaleString('default', { month: 'long', year: 'numeric' })}`,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Cumulative Amount ($)'
        },
        ticks: {
          callback: function(value) {
            return '$' + value.toFixed(2);
          }
        }
      },
      x: {
        title: {
          display: true,
          text: 'Day of Month'
        }
      }
    },
    interaction: {
      intersect: false,
      mode: 'index',
    },
  };

  if (loading) {
    return <div>Loading daily spending chart...</div>;
  }

  if (!chartData) {
    return <div>No data available</div>;
  }

  return (
    <div>
      <Line data={chartData} options={options} />
    </div>
  );
};

export default MonthlyTotalSpentChart;